import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, AccordionDetails, Stack, Box } from '@mui/material';
import { useIntl } from 'react-intl';

import { ConceptAccordion } from './sections/ConceptAccordion';
import { Criterion } from './sections/Criterion';
import { useCohortDefinitionLocators } from './useCohortDefinitionLocators';
import { useCohortDefinitionSummary } from './useCohortDefinitionSummary';
import { IndexDateSection } from '../IndexDateSection/IndexDateSection';
import { StudyPeriodSection } from '../StudyPeriodSection/StudyPeriodSection';
import { AccordionSummaryTitle } from './sections/AccordionSummaryTitle';

export const CohortDefinitionSummary = () => {
  const intl = useIntl();
  const {
    inclusionCriteria,
    exclusionCriteria,
    conceptGroups,
    events,
    expanded,

    handleAccordionChange,

    indexDateCount,
    studyPeriodCount,

    simpleEventIdsWithNonExistingConcepts,
    eventIdsWithMissingEventNames,
    eventIdsWithDuplicateEventNames,
    inclusionCriteriaIdsWithMissingEventNames,
    inclusionCriteriaIdsWithDuplicateNames,
    exclusionCriteriaIdsWithMissingEventNames,
    exclusionCriteriaIdsWithDuplicateNames,
  } = useCohortDefinitionSummary();

  const {
    indexDateAccordionId,
    indexDateAccordionSummaryId,
    indexDateAccordionDetailsId,

    studyPeriodAccordionId,
    studyPeriodAccordionSummaryId,
    studyPeriodAccordionDetailsId,

    inclusionCriteriaAccordionId,
    inclusionCriteriaAccordionSummaryId,
    inclusionCriteriaAccordionDetailsId,

    exclusionCriteriaAccordionId,
    exclusionCriteriaAccordionSummaryId,
    exclusionCriteriaAccordionDetailsId,

    eventsAccordionId,
    eventsAccordionSummaryId,
    eventsAccordionDetailsId,

    conceptGroupsAccordionId,
    conceptGroupsAccordionSummaryId,
    conceptGroupsAccordionDetailsId,
  } = useCohortDefinitionLocators();

  return (
    <Stack direction={'column'} spacing={4} width={'100%'}>
      <Box>
        {/* Index date accordion */}
        <Accordion
          data-testid={indexDateAccordionId}
          expanded={expanded === 6}
          onChange={handleAccordionChange(6)}
          sx={{ width: '100%' }}
          disableGutters
        >
          <AccordionSummary
            data-testid={indexDateAccordionSummaryId}
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionSummaryTitle
              title={intl.formatMessage({
                id: 'projects.cohortBuilderPage.setIndexDateDialog.titleSet',
              })}
              count={indexDateCount}
            />
          </AccordionSummary>

          <AccordionDetails data-testid={indexDateAccordionDetailsId}>
            <IndexDateSection minimalMode />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box>
        {/* Study period accordion */}
        <Accordion
          data-testid={studyPeriodAccordionId}
          expanded={expanded === 5}
          onChange={handleAccordionChange(5)}
          sx={{ width: '100%' }}
          disableGutters
        >
          <AccordionSummary
            data-testid={studyPeriodAccordionSummaryId}
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionSummaryTitle
              title={intl.formatMessage({
                id: 'projects.cohortBuilderPage.studyPeriodSection.title',
              })}
              count={studyPeriodCount}
            />
          </AccordionSummary>

          <AccordionDetails data-testid={studyPeriodAccordionDetailsId}>
            <StudyPeriodSection minimalMode />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box>
        {/* Inclusion criteria accordion */}
        <Accordion
          data-testid={inclusionCriteriaAccordionId}
          expanded={expanded === 1}
          onChange={handleAccordionChange(1)}
          sx={{ width: '100%' }}
          disableGutters
        >
          <AccordionSummary
            data-testid={inclusionCriteriaAccordionSummaryId}
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionSummaryTitle
              title="Inclusion criteria"
              count={inclusionCriteria?.length || 0}
              isErrorIconShown={
                inclusionCriteriaIdsWithMissingEventNames.length > 0 ||
                inclusionCriteriaIdsWithDuplicateNames.length > 0
              }
            />
          </AccordionSummary>

          <AccordionDetails data-testid={inclusionCriteriaAccordionDetailsId}>
            {inclusionCriteria?.map((criterion) => (
              <Criterion
                key={criterion.id}
                id={criterion.id}
                name={criterion.name}
                type={criterion.type}
                changeStatus={criterion.changeStatus}
                isErrorIconShown={
                  inclusionCriteriaIdsWithMissingEventNames.includes(criterion.id) ||
                  inclusionCriteriaIdsWithDuplicateNames.includes(criterion.id)
                }
              />
            ))}
          </AccordionDetails>
        </Accordion>

        {/* Exclusion criteria accordion */}
        <Accordion
          data-testid={exclusionCriteriaAccordionId}
          expanded={expanded === 2}
          onChange={handleAccordionChange(2)}
          sx={{ width: '100%' }}
          disableGutters
        >
          <AccordionSummary
            data-testid={exclusionCriteriaAccordionSummaryId}
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionSummaryTitle
              title="Exclusion criteria"
              count={exclusionCriteria?.length || 0}
              isErrorIconShown={
                exclusionCriteriaIdsWithMissingEventNames.length > 0 ||
                exclusionCriteriaIdsWithDuplicateNames.length > 0
              }
            />
          </AccordionSummary>

          <AccordionDetails data-testid={exclusionCriteriaAccordionDetailsId}>
            {exclusionCriteria?.map((criterion) => (
              <Criterion
                key={criterion.id}
                id={criterion.id}
                name={criterion.name}
                type={criterion.type}
                changeStatus={criterion.changeStatus}
                isErrorIconShown={
                  exclusionCriteriaIdsWithMissingEventNames.includes(criterion.id) ||
                  exclusionCriteriaIdsWithDuplicateNames.includes(criterion.id)
                }
              />
            ))}
          </AccordionDetails>
        </Accordion>

        {/* Events accordion */}
        <Accordion
          data-testid={eventsAccordionId}
          expanded={expanded === 3}
          onChange={handleAccordionChange(3)}
          sx={{ width: '100%' }}
          disableGutters
        >
          <AccordionSummary data-testid={eventsAccordionSummaryId} expandIcon={<ExpandMoreIcon />}>
            <AccordionSummaryTitle
              title="Events"
              count={events.length}
              isErrorIconShown={
                simpleEventIdsWithNonExistingConcepts.length > 0 ||
                eventIdsWithMissingEventNames.length > 0 ||
                eventIdsWithDuplicateEventNames.length > 0
              }
            />
          </AccordionSummary>

          <AccordionDetails data-testid={eventsAccordionDetailsId}>
            {events.map((criterion) => (
              <Criterion
                key={criterion.id}
                id={criterion.id}
                name={criterion.name}
                type={criterion.type}
                changeStatus={criterion.changeStatus}
                isErrorIconShown={
                  simpleEventIdsWithNonExistingConcepts.includes(criterion.id) ||
                  eventIdsWithMissingEventNames.includes(criterion.id) ||
                  eventIdsWithDuplicateEventNames.includes(criterion.id)
                }
              />
            ))}
          </AccordionDetails>
        </Accordion>

        {/* Concept groups accordion */}
        <Accordion
          data-testid={conceptGroupsAccordionId}
          expanded={expanded === 4}
          onChange={handleAccordionChange(4)}
          sx={{ width: '100%' }}
          disableGutters
        >
          <AccordionSummary
            data-testid={conceptGroupsAccordionSummaryId}
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionSummaryTitle title="Concept groups" count={conceptGroups.length} />
          </AccordionSummary>

          <AccordionDetails data-testid={conceptGroupsAccordionDetailsId}>
            {conceptGroups.map((concept) => (
              <ConceptAccordion key={concept.id} {...concept} />
            ))}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Stack>
  );
};
